import ExcelJS from "exceljs";
import {
  REMOVE_IGO,
  STEWARDSHIP_EXPORT_TURNAROUND_DAYS,
} from "@platform/feature-flags";

import type { StewardshipExportRow } from "./types";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Cell categories drive the Excel cell type and number format so the workbook
// is analysable: currency sums, dates sort chronologically, counts aggregate.
type CellKind = "text" | "number" | "integer" | "currency" | "date" | "boolean";

// Whole-dollar currency: premiums are large and cents add noise to a summary.
const CURRENCY_FORMAT = "$#,##0";

// ISO display so a date cell reads the same as the source data once typed.
const DATE_FORMAT = "yyyy-mm-dd";

// Whole-number counts: keep them numeric (so they aggregate) with no decimals.
const INTEGER_FORMAT = "#,##0";

// Parses an ISO `yyyy-mm-dd` into a local-midnight Date. Returns null for any
// value that is not a well-formed date string so the cell stays empty.
function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
}

type StewardshipExportColumn = {
  header: string;
  kind: CellKind;
  value: (row: StewardshipExportRow) => string | number | boolean | null;
  // When set, the column is written only if the named feature flag is enabled,
  // so a gated column can be rolled back without a code change.
  flag?: string;
  // When true, the column is dropped while REMOVE_IGO is on, so the IGO metric
  // can be hidden (and restored) without a code change.
  hiddenByRemoveIgo?: boolean;
};

// Column order is part of the export contract: consumers read the sheet by
// position, so the order here is the order they see.
export const STEWARDSHIP_EXPORT_COLUMNS: StewardshipExportColumn[] = [
  { header: "RFP ID", kind: "text", value: (row) => row.rfp_id },
  { header: "Broker ID", kind: "integer", value: (row) => row.broker_id },
  { header: "Client", kind: "text", value: (row) => row.client_name },
  { header: "Submitter", kind: "text", value: (row) => row.rfp_submitter_name },
  {
    header: "Submitter Email",
    kind: "text",
    value: (row) => row.rfp_submitter_email,
  },
  {
    header: "Outcome",
    kind: "text",
    value: (row) => row.resolved_outcome_label,
  },
  {
    header: "Submitted Date",
    kind: "date",
    value: (row) => row.rfp_date_submitted_by_broker,
  },
  {
    header: "Effective Date",
    kind: "date",
    value: (row) => row.rfp_plan_effective_date,
  },
  { header: "Due Date", kind: "date", value: (row) => row.rfp_due_date },
  {
    header: "In Good Order Date",
    kind: "date",
    value: (row) => row.rfp_in_good_order_date,
  },
  {
    header: "Sent To Carriers Date",
    kind: "date",
    value: (row) => row.rfp_date_initially_sent_to_carriers,
  },
  {
    header: "Initial Delivered Date",
    kind: "date",
    value: (row) => row.rfp_initial_delivered_date,
  },
  {
    header: "Complete Date",
    kind: "date",
    value: (row) => row.rfp_complete_date,
  },
  {
    header: "Days to IGO",
    kind: "integer",
    value: (row) => row.days_to_igo,
    hiddenByRemoveIgo: true,
  },
  {
    header: "Turnaround (Business Days)",
    kind: "integer",
    value: (row) => row.turnaround_time_business_days,
    flag: STEWARDSHIP_EXPORT_TURNAROUND_DAYS,
  },
  {
    header: "Carriers Quoted",
    kind: "integer",
    value: (row) => row.number_of_carriers_quoted,
  },
  {
    header: "Products Count",
    kind: "integer",
    value: (row) => row.products_count,
  },
  { header: "Hours Saved", kind: "number", value: (row) => row.hours_saved },
  { header: "Product ID", kind: "text", value: (row) => row.product_id },
  { header: "Product", kind: "text", value: (row) => row.product_type },
  {
    header: "Plans Quoted",
    kind: "integer",
    value: (row) => row.number_of_plans_quoted,
  },
  {
    header: "Plans Presented",
    kind: "integer",
    value: (row) => row.number_of_plans_presented,
  },
  {
    header: "Marketed Premium",
    kind: "currency",
    value: (row) => row.marketed_premium,
  },
  {
    header: "Finalized Premium",
    kind: "currency",
    value: (row) => row.finalized_premium,
  },
  {
    header: "Cross Sell Success",
    kind: "currency",
    value: (row) => row.new_line_premium,
  },
  {
    header: "Premium Out To Market",
    kind: "currency",
    value: (row) => row.premium_out_to_market,
  },
  {
    header: "Net New Client Premium",
    kind: "currency",
    value: (row) => row.net_new_client_premium,
  },
  {
    header: "Existing Client Add-On Premium",
    kind: "currency",
    value: (row) => row.existing_client_add_on_premium,
  },
  {
    header: "Open Pipeline: New Client",
    kind: "boolean",
    value: (row) => row.is_open_prospect,
  },
  {
    header: "Open Pipeline: Add-On",
    kind: "boolean",
    value: (row) => row.is_open_existing_client_add_on,
  },
  {
    header: "Incumbent Carrier",
    kind: "text",
    value: (row) => row.incumbent_carrier_name,
  },
  {
    header: "Incumbent Tier",
    kind: "text",
    value: (row) => row.incumbent_carrier_tier,
  },
  {
    header: "Winning Carrier",
    kind: "text",
    value: (row) => row.winning_carrier_name,
  },
  {
    header: "Winning Tier",
    kind: "text",
    value: (row) => row.winning_carrier_tier,
  },
  {
    header: "Retained Premium",
    kind: "currency",
    value: (row) => row.retained_premium,
  },
  {
    header: "Pending Premium",
    kind: "currency",
    value: (row) => row.pending_premium,
  },
  { header: "Pending Tier", kind: "text", value: (row) => row.pending_tier },
];

// Writes one typed cell. A missing value is left untouched so it stays an empty
// cell (never coerced to 0 or "null"), which keeps Excel aggregations honest.
function writeCell(
  cell: ExcelJS.Cell,
  kind: CellKind,
  value: string | number | boolean | null,
): void {
  if (value === null) {
    return;
  }

  switch (kind) {
    case "currency":
      cell.value = value;
      cell.numFmt = CURRENCY_FORMAT;
      return;
    case "integer":
      cell.value = value;
      cell.numFmt = INTEGER_FORMAT;
      return;
    case "date": {
      const date = typeof value === "string" ? parseIsoDate(value) : null;
      if (date === null) {
        return;
      }
      cell.value = date;
      cell.numFmt = DATE_FORMAT;
      return;
    }
    default:
      cell.value = value;
  }
}

// A gated column is written only when its flag is present and enabled; an
// ungated column always is. A column hidden by REMOVE_IGO is additionally
// dropped while that flag is on.
function selectColumns(
  enabledFlags: Record<string, boolean>,
): StewardshipExportColumn[] {
  const removeIgo = enabledFlags[REMOVE_IGO] === true;
  return STEWARDSHIP_EXPORT_COLUMNS.filter(
    (column) =>
      (column.flag === undefined || enabledFlags[column.flag] === true) &&
      !(column.hiddenByRemoveIgo && removeIgo),
  );
}

export function buildStewardshipWorkbook(
  rows: StewardshipExportRow[],
  enabledFlags: Record<string, boolean> = {},
): ExcelJS.Workbook {
  const columns = selectColumns(enabledFlags);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Stewardship Export");

  worksheet.addRow(columns.map((column) => column.header));
  worksheet.views = [{ state: "frozen", ySplit: 1 }];

  rows.forEach((row) => {
    const worksheetRow = worksheet.addRow([]);

    columns.forEach((column, index) => {
      writeCell(
        worksheetRow.getCell(index + 1),
        column.kind,
        column.value(row),
      );
    });
  });

  return workbook;
}

export async function buildStewardshipExportWorkbook(
  rows: StewardshipExportRow[],
  enabledFlags: Record<string, boolean> = {},
): Promise<Blob> {
  const buffer = await buildStewardshipWorkbook(
    rows,
    enabledFlags,
  ).xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}
